import { motion } from 'framer-motion';
import { Wifi, Wind, Armchair, Music, Battery, Shield } from 'lucide-react';

const FLEET_IMG = 'https://static.prod-images.emergentagent.com/jobs/e17238f4-6670-41c0-9f2d-00e20837d10a/images/63e87d95248d9bcf530868ac906a05a0859f7127118fc640cd3950bc2c85326f.png';

const amenities = [
  { icon: Wind, label: 'Climate Controlled AC' },
  { icon: Wifi, label: 'Free WiFi Onboard' },
  { icon: Armchair, label: 'Reclining Leather Seats' },
  { icon: Music, label: 'Premium Sound System' },
  { icon: Battery, label: 'USB Charging Ports' },
  { icon: Shield, label: 'Full Insurance Coverage' },
];

const audiences = [
  { title: 'Families', desc: 'Kid-friendly with sit-down lunch. All ages welcome.', color: '#FF007A' },
  { title: 'Couples', desc: 'Romantic Hollywood getaway from Las Vegas.', color: '#9D00FF' },
  { title: 'Convention Companions', desc: 'Perfect escape while they network.', color: '#00E5FF' },
  { title: 'Church Groups', desc: 'Group rates for 15+ guests starting at $185.', color: '#FFD600' },
  { title: 'Solo Adventurers', desc: 'Meet fellow travelers on a small group tour.', color: '#FF007A' },
  { title: 'Birthday Celebrations', desc: 'Hollywood is the ultimate party backdrop.', color: '#00E5FF' },
];

const Fleet = () => {
  return (
    <section className="section-padding bg-white" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FF007A] mb-2">Travel in Comfort & Style</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">Our Premium Fleet</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="rounded-xl overflow-hidden border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          >
            <img
              src={FLEET_IMG}
              alt="Luxury tour bus for Las Vegas to Hollywood sightseeing tours"
              className="w-full h-64 sm:h-80 object-cover"
              loading="lazy"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-bebas text-2xl sm:text-3xl tracking-tight uppercase mb-4">Luxury Vehicles, Every Trip</h3>
            <p className="font-outfit text-base text-[#4A4A4A] mb-6 leading-relaxed">
              Whether you are joining a small group in our Mercedes Sprinter vans or booking a private SUV for up to 7 guests,
              every vehicle in our fleet is equipped with premium amenities for maximum comfort on your California adventure.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {amenities.map((item, i) => (
                <div key={i} className="flex items-center gap-2 font-outfit text-sm" data-testid={`amenity-${i}`}>
                  <item.icon className="w-4 h-4 text-[#FF007A]" />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FF007A] mb-2">Perfect for Everyone</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">Who Loves Our Tours?</h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {audiences.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="card-brutal p-5 flex items-start gap-4"
              data-testid={`audience-card-${i}`}
            >
              <div className="w-3 h-3 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: item.color }} />
              <div>
                <h4 className="font-bebas text-lg tracking-tight uppercase">{item.title}</h4>
                <p className="font-outfit text-sm text-[#4A4A4A]">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Fleet;
